import React, { useState, Component } from 'react';
/**
 * ExplanationContent — Conversational explanation with Djay intros.
 *
 * Shows distractor explanation (why wrong) and main explanation (how to solve)
 * with Djay avatar intros and numbered solution steps.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DjayIdentity, getDjayDistractorIntro, getDjayExplanationIntro } from '../../../mascot';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationContentProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationContent({
  explanation,
  distractorExplanation,
  isCorrect,
  solutionSteps
}: ExplanationContentProps) {
  // useState initializer runs once — prevents random re-pick on scroll/re-render
  const [distractorIntro] = useState(() => getDjayDistractorIntro());
  const [explanationIntro] = useState(() => getDjayExplanationIntro());
  return <div className="space-y-5" data-id="element-10733" data-mp="25ecea6cjhmw3">
      {/* Distractor explanation (why it was wrong) */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-10734" data-mp="20f9k5gy3l12w">
          <div className="flex items-start gap-3 mb-4" data-id="element-10735" data-mp="1mfqd6rh0vvs6">
            <motion.div className="flex-shrink-0 mt-0.5" animate={{
          rotate: [0, -5, 5, 0]
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} data-id="element-10736" data-mp="1mg9ymi5vt2ay">
              <DjayIdentity size="sm" emotion="thinking" animate={true} data-id="element-10737" data-mp="3bvzn8r785csa" />
            </motion.div>
            <div className="min-w-0 flex-1" data-id="element-10738" data-mp="3uyi80b7k66on">
              <h4 className="font-bold text-slate-700 text-sm mb-2" data-id="element-10739" data-mp="3uxqbmy6504xb">
                {distractorIntro}
              </h4>
              <div className="text-slate-600 text-sm leading-relaxed" data-id="element-10740" data-mp="3lz4vtas1v408">
                <LatexRenderer content={distractorExplanation} data-id="element-10741" data-mp="3lws7mfvp7xeq" />
              </div>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-5" data-id="element-10742" data-mp="ii68mrtbazhj" />
        </motion.div>}

      {/* Main explanation (how to solve it) */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: distractorExplanation ? 0.15 : 0,
      duration: 0.3
    }} className="relative" data-id="element-10743" data-mp="mhnehmcnhyn8">
        <div className="flex items-center gap-2 mb-3" data-id="element-10744" data-mp="2tcagste04wu5">
          <motion.div animate={{
          y: [0, -2, 0]
        }} transition={{
          delay: 0.4,
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-10745" data-mp="2yabwd4c7ye0v">
            <DjayIdentity size="sm" emotion="pointing" animate={true} data-id="element-10746" data-mp="2zq7l4gbd46ng" />
          </motion.div>
          <span className="text-sm font-bold text-orange-600" data-id="element-10747" data-mp="2urd3hhp4so4x">
            {explanationIntro}
          </span>
        </div>

        <ExplanationSteps text={explanation || (isCorrect ? 'Goed gedaan! Je hebt de juiste strategie gebruikt.' : 'Kijk goed naar de stappen hierboven.')} steps={solutionSteps} variant="conversational" data-id="element-10748" data-mp="2710o5lrk3p4b" />
      </motion.div>
    </div>;
}